import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TermsPage = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-[#262626]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-['Oswald'] text-xl md:text-2xl font-bold tracking-tight">
            WILTSHIRE<span className="text-[#FF4500]">WEB</span><span className="text-[#888888] font-normal ml-1">STUDIO</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#888888] hover:text-white">
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="font-['Oswald'] text-4xl md:text-5xl text-white uppercase mb-4">
            Terms and Conditions
          </h1>
          <p className="font-['Manrope'] text-[#888888] mb-12">
            Last updated: April 2026
          </p>

          <div className="prose prose-invert max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">1. About these Terms</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                These Terms and Conditions ("Terms") govern the supply of web design, development, hosting, maintenance and related services by Wiltshire Web Studio ("we", "us", "our") to you, our client ("you", "your"). By accepting a quote or instructing us to begin work, you confirm that you have read, understood and agreed to these Terms.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Wiltshire Web Studio is based in Wiltshire, United Kingdom. You can contact us at george@wiltshirewebstudio.com or on 07767 071 806.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Our services are supplied to business clients only. By engaging us, you confirm that you are acting in the course of a business (including as a sole trader, partnership, limited company or other business entity) and not as a consumer.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                A contract is formed between us when you accept our proposal in writing (including by email). The prices and scope that apply to your Project are those set out in our pricing guide or proposal at the time you accept.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">2. Scope of Work</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                The work we will carry out for you is defined by the package or proposal you have accepted, as set out in our pricing guide. Any work outside that agreed scope (including extras, additional features, or changes beyond your 4 free changes) will be charged separately at the rates shown in our pricing guide.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">3. Fees and Payment</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mb-4">
                Unless agreed otherwise in writing:
              </p>
              <ul className="font-['Manrope'] text-[#CCCCCC] leading-relaxed space-y-2 list-disc pl-6">
                <li>No deposit is required to begin work on a standard build package.</li>
                <li>Once your website is completed and taken live, the full Project fee (including any agreed extras) is due and payable immediately on receipt of our invoice.</li>
                <li>If payment is not received within 7 days of the website going live, we will take the website offline and keep it offline until payment has been received in full.</li>
                <li>Monthly hosting and maintenance plans are invoiced in advance. Yearly plans are non-refundable once the period has started.</li>
                <li>If a monthly hosting payment is missed, you will have 7 days to make payment. After that, a late fee of £10 will be added and we may suspend the website until the balance is paid.</li>
                <li>Additional changes beyond your 4 free changes are charged at £20 per hour, with a minimum charge of 1 hour. This hourly rate does not apply on top of any extras purchased at fixed prices from our pricing guide.</li>
              </ul>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                All prices include VAT where applicable. We reserve the right to charge interest on overdue amounts at 8% above the Bank of England base rate under the Late Payment of Commercial Debts (Interest) Act 1998.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Any website taken offline due to non-payment will be restored once cleared payment is received. We are not liable for any loss of business, traffic, rankings or revenue during any period the site is offline due to non-payment.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">4. Client Responsibilities</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                You agree to provide all content, feedback, approvals and access credentials we reasonably need, in a timely manner. We are not liable for delays caused by late or missing responses from you. If you do not provide what we need within 30 days of our request, we may treat the Project as complete and invoice any outstanding balance.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">5. Intellectual Property</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                On full payment of all fees, you own the final visual design, graphics, and written content produced specifically for you. You are granted a licence to use the completed website for your business purposes.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                All source code, underlying frameworks, templates and technical implementation remain our property. We do not provide or transfer website code to clients. Third-party software (themes, plugins, fonts, stock imagery) remains the property of its respective owners under their own licence terms.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Until full payment is received, all intellectual property rights remain with us.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                You grant us permission to use your name, logo and screenshots of the completed website in our portfolio and marketing materials, unless you tell us in writing that you would prefer us not to.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">6. Limitation of Liability</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                Nothing in these Terms excludes our liability for death or personal injury caused by our negligence, or for fraud.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Subject to the above, our total liability to you in connection with the Project is limited to the total fees you have paid us in the 12 months before the claim arose. We are not liable for any loss of profits, business, revenue, data, goodwill, or any indirect or consequential loss.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                You are responsible for maintaining your own backups of content you provide to us or store on our servers.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">7. Cancellation</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                You may cancel at any time by written notice. You will be invoiced for all work completed to that date on a fair pro-rata basis, plus any third-party costs committed on your behalf. No rights in the Deliverables transfer until all invoices are paid.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Monthly hosting plans can be cancelled with 30 days' written notice. Yearly plans run to the end of their paid term and are non-refundable.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                We may terminate by written notice if you fail to pay on time, fail to provide required content or feedback within a reasonable period, or materially breach these Terms. On termination, all outstanding fees become immediately payable.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">8. Data Protection</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                We process personal data in accordance with the UK GDPR and the Data Protection Act 2018. Where we process personal data on your behalf (for example, through contact forms we build), we act as a processor and you act as the controller.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">9. Governing Law</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute arising from them.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">10. Contact</h2>
              <div className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                <p className="text-white font-semibold">George - Wiltshire Web Studio</p>
                <p>Email: <a href="mailto:george@wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">george@wiltshirewebstudio.com</a></p>
                <p>Phone: <a href="tel:07767071806" className="text-[#FF4500] hover:underline">07767 071 806</a></p>
                <p>Web: <a href="https://www.wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">www.wiltshirewebstudio.com</a></p>
              </div>
            </section>
          </div>

          {/* Back to home */}
          <div className="mt-16 pt-8 border-t border-[#262626]">
            <Link to="/">
              <Button className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider">
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsPage;
